import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { isEmpty, isEqual } from "lodash";
import { Char } from "@evergis/condition";
import { AttributeType, FeatureDc, UpdateFeatureDc } from "@evergis/api";
import { useToggle } from "@evergis/ui";

import { AttributesUpdate, useAttributes } from "../../../hooks/attributes/useAttributes";
import { resetEditFeatureChanges, setAttributes } from "../../../ducks/editFeature/editFeature";
import { resetGeometryEditor } from "../../../ducks/geometryEditor/geometryEditor";
import { useNotification } from "../../../hooks/notifications/useNotification";
import { useShowFeatureCard } from "../../Table/hooks/useShowFeatureCard";
import { useFeatureEditHistory } from "./useFeatureEditHistory";
import { usePostgresLayerTableName } from "../../../hooks/layer/usePostgresLayerWithTable";
import { DateFormat, formatDate } from "../../../utils/date";
import { useCreateFeature } from "../../../hooks/feature/useCreateFeature";
import { useUpdateFeature } from "../../../hooks/feature/useUpdateFeature";

import { FeatureCardMode } from "../types";
import { FeatureDcExtended } from "../../../ducks/selectFeatures/types";
import { FeatureAttribute, FeatureAttributeValue } from "../components/Attributes/FeatureAttributeField";

type FeatureAttributesEdit = [
  FeatureAttribute[],
  {
    updateAttribute: AttributesUpdate<FeatureAttribute>["updateAttribute"];
    saveAttributes: VoidFunction;
    storeAttributes: VoidFunction;
  },
];

export const useFeatureAttributes = (
  feature: Nullable<FeatureDcExtended>,
  isEdit: boolean,
  mode: FeatureCardMode,
  layerName?: string,
  toggleEditMode?: (withSave?: boolean) => void,
): FeatureAttributesEdit => {
  const { t } = useTranslation("featureCard");
  const dispatch = useDispatch();
  const currentLayerName = feature?.layer || layerName;
  const tableName = usePostgresLayerTableName(currentLayerName);
  const [updateFeature, { data: updateData }] = useUpdateFeature(currentLayerName);
  const [createFeature, { data, error }] = useCreateFeature(currentLayerName, tableName);
  const [{ attributes: currentAttributes }] = useFeatureEditHistory();
  const [attributesState, { updateAttribute, setAttributes: setAttributesState }] = useAttributes(currentAttributes);
  const [isChangedGeometry, toggleChangedGeometry] = useToggle();
  const prevCoordinates = useRef(feature?.geometry?.coordinates);
  const showFeature = useShowFeatureCard(currentLayerName);

  const updateLayer = useCallback(
    (withSave?: boolean) => {
      dispatch(resetGeometryEditor());
      dispatch(resetEditFeatureChanges(withSave));
    },
    [dispatch],
  );

  const storeAttributes = useCallback(() => dispatch(setAttributes(attributesState)), [attributesState, dispatch]);

  const saveAttributes = useCallback(() => {
    const createMode = mode === FeatureCardMode.Create;
    const formattedAttributes = formatAttributes(attributesState || [], feature?.attributes);
    const newFeature: UpdateFeatureDc = { id: feature?.id };

    if (createMode || !isEmpty(formattedAttributes)) {
      newFeature.attributes = formattedAttributes;
    }

    if (createMode || isChangedGeometry) {
      newFeature.geometry = feature?.geometry as UpdateFeatureDc["geometry"];
      toggleChangedGeometry();
    }

    if (createMode) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      createFeature({ ...feature, ...(newFeature as FeatureDc) }).then(() => {
        updateLayer(true);
      });
    } else {
      updateFeature(newFeature as FeatureDc).then(() => {
        updateLayer(true);
        toggleEditMode?.(true);
      });
    }
  }, [
    mode,
    attributesState,
    feature,
    isChangedGeometry,
    toggleChangedGeometry,
    createFeature,
    updateLayer,
    updateFeature,
    toggleEditMode,
  ]);

  useNotification(data && data.layerName, {
    title: t("objectCreated"),
    success: true,
  });

  useNotification(error, {
    title: t("objectCreationError"),
    error: true,
  });

  useEffect(() => {
    setAttributesState(currentAttributes);
  }, [feature, isEdit, currentAttributes]);

  useEffect(() => {
    if (!isChangedGeometry && !isEqual(feature?.geometry?.coordinates, prevCoordinates.current)) {
      toggleChangedGeometry();
      prevCoordinates.current = feature?.geometry?.coordinates;
    }
  }, [feature?.geometry?.coordinates]);

  useEffect(() => {
    if (data && data.result?.createdIds?.length > 0) {
      isEdit && toggleEditMode();
      showFeature(data.result?.createdIds[0] as unknown as string, true);
    }
  }, [data]);

  useEffect(() => {
    if (updateData && updateData.updatedIds?.length > 0) {
      showFeature(updateData.updatedIds[0] as unknown as string, true);
    }
  }, [updateData]);

  return [attributesState || [], { updateAttribute, saveAttributes, storeAttributes }];
};

export const unEscapeAttributeValue = <Value extends FeatureAttributeValue>(value: Value): Value | string =>
  typeof value === "string" ? Char.unEscapeSpecChars(value) : value;

export function formatAttributes(
  attributesState: FeatureAttribute[],
  oldAttributes: FeatureDc["attributes"],
): Record<string, string> {
  const filtered = oldAttributes
    ? attributesState.filter(state => state.value !== oldAttributes[state.name])
    : attributesState;

  return filtered.reduce(
    (acc, attribute) => ({
      ...acc,
      [attribute.name]:
        attribute.type === AttributeType.DateTime && attribute.value !== undefined
          ? formatDate(attribute.value as Date, {
              dateFormat: DateFormat.UTC,
              defaultValue: attribute.value as Date,
            })
          : attribute.value,
    }),
    {},
  );
}
